import { useMemo } from 'react';

import type { IFarm } from 'interfaces/IFarm';
import { InputType, type IInput } from 'interfaces/IInput';
import { excludeHiddenTreeEntities } from 'utils/FarmUtils';

import { useFetchInputsByOrgId } from 'hooks/inputs/useFetchInputsByOrgId';
import { useFetchFarmsByOrgId } from 'hooks/useFetchFarmsByOrgId';

const EMPTY_FARMS: IFarm[] = [];
const EMPTY_INPUTS: IInput[] = [];

/**
 * The organisation's farms and PlanActual plans with excluded farms and
 * paddocks — and every plan linked to one — removed (OP-2384, OP-2390).
 *
 * Every view that shows farm, paddock or plan data reads through this hook,
 * so an excluded item is hidden everywhere (tree, Insights, Diagnostics at
 * any time range, Live totals, links), not only where scope comes from the
 * tree. Only what's displayed is filtered: the React Query cache and saved
 * records are untouched, so un-excluding brings an item back.
 *
 * Plans are withheld until farms have loaded, since exclusion is decided by
 * the farm and paddock records — otherwise an excluded plan could flash up.
 * The raw query results are returned for their loading and error flags.
 */
export const useVisibleOrgData = (orgId: string | null) => {
  const farmsQuery = useFetchFarmsByOrgId(orgId);
  const inputsQuery = useFetchInputsByOrgId(orgId, InputType.PlanActual);

  const { farms, inputs } = useMemo(
    () =>
      farmsQuery.data
        ? excludeHiddenTreeEntities(
            farmsQuery.data,
            inputsQuery.data ?? EMPTY_INPUTS
          )
        : { farms: EMPTY_FARMS, inputs: EMPTY_INPUTS },
    [farmsQuery.data, inputsQuery.data]
  );

  return { farms, inputs, farmsQuery, inputsQuery };
};
